import { useRef, useState, useCallback } from "react";
import { useAuthToken } from "../lib/authToken";
import { WS_URL } from "../lib/env";
function withAuthToken(url, token) {
    if (!token)
        return url;
    const wsUrl = new URL(url);
    wsUrl.searchParams.set("token", token);
    return wsUrl.toString();
}
export function useSession() {
    const getAuthToken = useAuthToken();
    const wsRef = useRef(null);
    const [sessionId, setSessionId] = useState(null);
    const [transcripts, setTranscripts] = useState([]);
    const [currentSuggestion, setCurrentSuggestion] = useState("");
    const [isStreaming, setIsStreaming] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const connect = useCallback(async (sessionMode = "meeting") => {
        try {
            setError(null);
            const wsUrl = `${WS_URL}/ws`;
            const token = await getAuthToken();
            const ws = new WebSocket(withAuthToken(wsUrl, token));
            wsRef.current = ws;
            ws.onopen = () => {
                setIsConnected(true);
                ws.send(JSON.stringify({
                    type: "start_session",
                    platform: "web",
                    mode: sessionMode,
                }));
            };
            ws.onmessage = (event) => {
                const msg = JSON.parse(event.data);
                switch (msg.type) {
                    case "session_started":
                        setSessionId(msg.sessionId);
                        break;
                    case "transcript":
                        setTranscripts((prev) => {
                            const filtered = msg.entry.isFinal
                                ? prev.filter((t) => t.isFinal || t.id !== msg.entry.id)
                                : prev.filter((t) => t.isFinal);
                            return [...filtered.slice(-49), msg.entry];
                        });
                        break;
                    case "suggestion_start":
                        setCurrentSuggestion("");
                        setIsStreaming(true);
                        break;
                    case "suggestion_token":
                        setCurrentSuggestion((prev) => prev + msg.token);
                        break;
                    case "suggestion_end":
                        setIsStreaming(false);
                        break;
                    case "error":
                        setError(msg.message);
                        break;
                }
            };
            ws.onclose = () => {
                setIsConnected(false);
                wsRef.current = null;
            };
            ws.onerror = () => setError("Connection failed");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Connection failed");
            throw err;
        }
    }, [getAuthToken]);
    const sendAudioConfig = useCallback((sampleRate, channels) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: "audio_config", sampleRate, channels }));
        }
    }, []);
    const sendAudioChunk = useCallback((buffer) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            const bytes = new Uint8Array(buffer);
            let binary = "";
            for (let i = 0; i < bytes.byteLength; i++)
                binary += String.fromCharCode(bytes[i]);
            const base64 = btoa(binary);
            wsRef.current.send(JSON.stringify({ type: "audio_chunk", data: base64 }));
        }
    }, []);
    const sendOCR = useCallback((text) => {
        wsRef.current?.send(JSON.stringify({ type: "ocr_text", text }));
    }, []);
    const askQuestion = useCallback((question = "", action = "assist") => {
        if (wsRef.current?.readyState !== WebSocket.OPEN) {
            setError("Session is not connected.");
            return false;
        }
        setError(null);
        setCurrentSuggestion("");
        setIsStreaming(true);
        wsRef.current.send(JSON.stringify({ type: "user_question", question, action }));
        return true;
    }, []);
    const disconnect = useCallback(() => {
        wsRef.current?.send(JSON.stringify({ type: "end_session" }));
        wsRef.current?.close();
        setIsConnected(false);
        setSessionId(null);
    }, []);
    return {
        sessionId,
        transcripts,
        currentSuggestion,
        isStreaming,
        isConnected,
        error,
        connect,
        disconnect,
        sendAudioChunk,
        sendAudioConfig,
        sendOCR,
        askQuestion,
    };
}
